import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import Hidden from '@material-ui/core/Hidden'
import Typography from '@material-ui/core/Typography'
import { DateTime } from 'luxon'
import useIconAccessor from 'components/lesson-type-category/icons'
import { parseEvent } from 'utils/eventUtils'
import SessionDetailsDialog from 'components/session-details-dialog'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import {
  SCHEDULE_EVENT_TYPES,
  getScheduleEventBadgeStyles,
  getScheduleEventColor,
  getScheduleEventPastAccentColor,
  getScheduleEventPastBadgeStyles,
  getScheduleEventType,
} from './schedule-event-colors'
import useStyles from './styles'

const getDurationMinutes = (startDateTime, endDateTime) =>
  Math.round(
    DateTime.fromISO(endDateTime)
      .diff(DateTime.fromISO(startDateTime), 'minutes')
      .toObject().minutes,
  )

const formatDuration = minutes => {
  if (minutes >= 60 && minutes % 60 === 0) {
    return `${minutes / 60}hr`
  }

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}hr ${remainingMinutes}m`
  }

  return `${minutes}m`
}

const getFacilityDescription = booking => booking.coachFacility?.name || null

const getSessionDisplay = (session, iconAccessor) => {
  const student = session.enrollments[0]?.student
  const category = session.lessonType?.category
  const categoryMeta = category ? iconAccessor[category] : null
  const fullName = student && `${student.firstName} ${student.lastName}`.trim()

  const name =
    fullName && session.title
      ? `${session.title} with ${fullName}`
      : fullName || session.title

  return {
    name,
    badgeLabel: categoryMeta?.text || 'Private',
    description: getFacilityDescription(session),
    eventType: SCHEDULE_EVENT_TYPES.PRIVATE,
  }
}

const getGroupEventDisplay = (groupEvent, iconAccessor) => {
  const eventType = getScheduleEventType(groupEvent)

  const badgeLabels = {
    [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: 'Family Golf',
    [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: 'PGA Jr. League',
    [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: iconAccessor.GROUP.text,
  }

  return {
    name: groupEvent.title,
    badgeLabel: badgeLabels[eventType] || iconAccessor.GROUP.text,
    description: getFacilityDescription(groupEvent),
    eventType,
  }
}

const getBlockedTimeDisplay = blockTime => ({
  name: blockTime.title || 'Blocked time',
  badgeLabel: 'Blocked',
  description: getFacilityDescription(blockTime),
  eventType: SCHEDULE_EVENT_TYPES.BLOCKED_TIME,
})

const getBookingDisplay = (booking, iconAccessor) => {
  if (getScheduleEventType(booking) === SCHEDULE_EVENT_TYPES.BLOCKED_TIME) {
    return getBlockedTimeDisplay(booking)
  }

  if (booking.__typename === 'Session') {
    return getSessionDisplay(booking, iconAccessor)
  }

  return getGroupEventDisplay(booking, iconAccessor)
}

const ScheduleRow = ({
  booking,
  isUpNext,
  interactive = true,
  showDateLabel = true,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useStyles()
  const iconAccessor = useIconAccessor()
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false)
  const [groupDetailsView, setGroupDetailsView] = useState(false)

  const { startDateTime, endDateTime, timezone, isCanceled } = booking
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  const end = DateTime.fromISO(endDateTime).setZone(timezone)
  const durationMinutes = getDurationMinutes(startDateTime, endDateTime)
  const isToday = start.hasSame(DateTime.now().setZone(timezone), 'day')
  const isPast = end <= DateTime.now().setZone(timezone)

  const display = getBookingDisplay(booking, iconAccessor)
  const accentColor = isPast
    ? getScheduleEventPastAccentColor(display.eventType)
    : getScheduleEventColor(display.eventType)
  const badgeStyles = isPast
    ? getScheduleEventPastBadgeStyles(display.eventType)
    : getScheduleEventBadgeStyles(display.eventType)

  const handleClick = () => {
    if (booking.__typename === 'Session') {
      setSessionDialogOpen(true)
      return
    }

    if (display.eventType === SCHEDULE_EVENT_TYPES.BLOCKED_TIME) {
      return
    }

    setGroupDetailsView('detail')
  }

  if (isCanceled) {
    return null
  }

  return (
    <>
      <Box
        className={`${classes.row} ${isPast ? classes.rowPast : ''} ${
          interactive ? '' : classes.rowStatic
        }`}
        onClick={interactive ? handleClick : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleClick()
              }
            }
            : undefined
        }
        data-testid={
          booking.__typename === 'Session'
            ? 'session-list-item'
            : 'schedule-row'
        }
      >
        <Box className={classes.timeColumn}>
          {showDateLabel && !isToday ? (
            <Typography className={classes.dateLabel}>
              {start.toFormat('ccc, MMM d')}
            </Typography>
          ) : null}
          <Hidden smUp>
            <Typography className={classes.compactTimeRange}>
              {`${start.toFormat('h:mm')}–${end.toFormat('h:mm a')}`}
            </Typography>
          </Hidden>
          <Hidden xsDown>
            <Typography className={classes.startTime}>
              {start.toFormat('h:mm a')}
            </Typography>
            <Typography className={classes.endTime}>
              {end.toFormat('h:mm a')}
            </Typography>
          </Hidden>
        </Box>

        <Box className={classes.contentCluster}>
          <Box
            className={classes.accentBar}
            style={{ backgroundColor: accentColor }}
          />

          <Box className={classes.detailsColumn}>
            <Box className={classes.nameRow}>
              <Box className={classes.titleBlock}>
                <Box className={classes.titleLine}>
                  <Typography component="div" className={classes.name}>
                    {display.name}
                  </Typography>
                </Box>
                <Hidden smUp>
                  <Box className={classes.categoryBadge} style={badgeStyles}>
                    {display.badgeLabel}
                  </Box>
                  {display.description ? (
                    <Typography className={classes.description}>
                      {display.description}
                    </Typography>
                  ) : null}
                </Hidden>
              </Box>
              {isUpNext && !isPast ? (
                <Box className={classes.upNextBadge}>UP NEXT</Box>
              ) : null}
            </Box>
            <Box className={classes.metaRow}>
              <Box className={classes.categoryBadge} style={badgeStyles}>
                {display.badgeLabel}
              </Box>
              {display.description ? (
                <Typography className={classes.description}>
                  {display.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Hidden smUp>
            <ChevronRightIcon className={classes.chevronMobile} />
          </Hidden>
        </Box>

        <Box className={classes.trailingColumn}>
          <Box className={classes.duration}>
            <AccessTimeIcon className={classes.durationIcon} />
            <Typography className={classes.durationText}>
              {formatDuration(durationMinutes)}
            </Typography>
          </Box>
          <Hidden xsDown>
            <ChevronRightIcon className={classes.chevron} />
          </Hidden>
        </Box>
      </Box>

      {interactive && booking.__typename === 'Session' ? (
        <SessionDetailsDialog
          isOpen={sessionDialogOpen}
          setIsOpen={setSessionDialogOpen}
          booking={booking}
          onReschedule={onReschedule}
          onPayment={onPayment}
          onCancellation={onCancellation}
        />
      ) : interactive &&
      display.eventType !== SCHEDULE_EVENT_TYPES.BLOCKED_TIME ? (
        <EventDetailsDialog
          isOpen={groupDetailsView}
          setIsOpen={setGroupDetailsView}
          event={parseEvent(booking)}
        />
      ) : null}
    </>
  )
}

export default ScheduleRow
